import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  type ColumnDef,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import clsx from "clsx";
import rg4js from "raygun4js";
import React, { useMemo, useState } from "react";
import { Form } from "react-bootstrap";
import { ChevronDown, ChevronUp } from "react-bootstrap-icons";
import { Link } from "react-router-dom";
import {
  type Client,
  CommunicationStatus,
  type DocumentDetail,
  DocumentStatus,
  ParentRelationship,
  Source,
  type ViewCommunication,
} from "@/briefpoint-client";
import Button from "@/components/Button";
import DocumentActionsMenu from "@/components/DocumentList/DocumentActionsMenu";
import { EditableText } from "@/components/EditableText/EditableText";
import Loading from "@/components/Loading";
import ReminderEmailModal from "@/components/Modals/ReminderEmailModal";
import Pagination from "@/components/Pagination/Pagination";
import {
  Table,
  TableBody,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "@/components/ui/toast";
import {
  useClientApi,
  useCommunicationApi,
  useDocumentApi,
  useFirmApi,
} from "@/hooks/useApi";
import { useAuth } from "@/hooks/useAuth";
import WordIcon from "@/images/new-doc-icon.svg?react";
import PdfIcon from "@/images/new-pdf-icon.svg?react";
import { CaliforniaId, SHOW_REMINDER_EMAIL_STATUSES } from "@/lib/constants";
import dayjs from "@/lib/dayjs";
import {
  getFirmCasesOptions,
  getFirmCommunicationsOptions,
  getUnarchivedCaseDocumentListOptions,
  getUnarchivedDocumentListOptions,
} from "@/lib/query-options";
import type { DocumentClient, ViewCommunicationResponse } from "@/lib/types";
import {
  addUploadDate,
  getCaseJurisdiction,
  getSubTypeText,
  normalizeError,
  sortCommunications,
  toLocaleUSDateString,
} from "@/lib/utils";
import AddToCalendarModal from "../DocumentList/AddToCalendarModal";
import EmptyList from "../DocumentList/EmptyList";
import { FormModal } from "../Modals/Modal";
import { UserNotificationIndicator } from "../UserNotificationIndicator";
import { DocumentAction } from "./DocumentAction";
import styles from "./DocumentListTable.module.scss";
import DocumentListTableRow from "./DocumentListTableRow";
import DocumentListTableRowCommunication from "./DocumentListTableRowCommunication";
import DocumentListTableSupplementalRow from "./DocumentListTableSupplementalRow";

type Props = {
  recent?: boolean;
  caseId?: string;
  nameFilter?: string;
  partyFilter?: string[];
  docFilter?: string[];
  clientFilter?: string[];
  chooseFile?: (e: React.MouseEvent) => void;
};

export default function DocumentListTable({
  recent = false,
  caseId,
  nameFilter,
  partyFilter,
  docFilter,
  clientFilter,
  chooseFile,
}: Props) {
  const queryClient = useQueryClient();
  const documentApi = useDocumentApi();
  const firmApi = useFirmApi();
  const clientApi = useClientApi();
  const communicationApi = useCommunicationApi();
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [selectedClient, setSelectedClient] = useState<Client | undefined>();
  /* used to determine if the client activity row is expanded - this must be done here to allow row below table row */
  const [expandedRows, setExpandedRows] = useState<string[]>([]);
  const [editDueDateDocument, setEditDueDateDocument] = useState<
    DocumentClient | undefined
  >();
  const [addToCalendarDocument, setAddToCalendarDocument] = useState<
    DocumentClient | undefined
  >();
  const [dueDateOverride, setDueDateOverride] = useState<string | undefined>();
  const { firm, jurisdictions, user } = useAuth();

  function canSendReminderEmail(
    communication: ViewCommunicationResponse | undefined,
  ) {
    return (
      (communication?.status &&
        SHOW_REMINDER_EMAIL_STATUSES.includes(communication.status)) ??
      false
    );
  }

  const communicationsQuery = useQuery({
    ...getFirmCommunicationsOptions(communicationApi, user?.firmId ?? ""),
    select: (data) =>
      data.map((c) => c as ViewCommunicationResponse).sort(sortCommunications),
  });

  const casesQuery = useQuery({
    ...getFirmCasesOptions(firmApi, user?.firmId ?? ""),
  });

  const clientsQuery = useQuery({
    queryKey: ["clientApi.clientGetAll", user?.firmId],
    queryFn: async () => {
      return await clientApi.clientGetAll({
        firmId: user?.firmId ?? "",
      });
    },
  });

  const caseJurisdiction = useMemo(() => {
    const caseJurisdiction = casesQuery.data?.find(
      (c) => c.id === caseId,
    )?.jurisdiction;
    if (!jurisdictions || !caseJurisdiction) {
      return undefined;
    }
    return jurisdictions.find((j) => j.id === caseJurisdiction);
  }, [jurisdictions, casesQuery.data, caseId]);

  const jxsWithDueDates = useMemo(() => {
    return jurisdictions?.filter((j) => j.supportsDueDates).map((j) => j.id);
  }, [jurisdictions]);

  const casesSupportingDueDates = useMemo(() => {
    return casesQuery.data?.filter(
      (c) => c.jurisdiction && jxsWithDueDates?.includes(c.jurisdiction),
    );
  }, [casesQuery, jxsWithDueDates]);

  const showDueDates = useMemo(() => {
    if (caseJurisdiction) {
      return caseJurisdiction.supportsDueDates;
    }

    return casesSupportingDueDates?.length;
  }, [caseJurisdiction, casesSupportingDueDates]);

  function filterCompletedDocuments(document: DocumentClient) {
    return !caseId ? document.status > DocumentStatus.Processed : true;
  }

  function editDueDate(document: DocumentClient) {
    setEditDueDateDocument(document);
    setDueDateOverride(undefined);
  }

  function closeDueDateModal() {
    setDueDateOverride(undefined);
    setEditDueDateDocument(undefined);
  }

  function addDueDateDays(document: DocumentClient, days?: number) {
    const existing = new Date(
      document.info?.dueDateOverridden || document.info?.dueDate || "",
    );

    const newDate = dayjs(existing).add(days ?? 1, days ? "day" : "month");
    setDueDateOverride(newDate.format("YYYY-MM-DD"));
  }

  const documentsQuery = useQuery({
    ...(caseId
      ? getUnarchivedCaseDocumentListOptions(documentApi, caseId ?? "")
      : getUnarchivedDocumentListOptions(documentApi)),
    notifyOnChangeProps: ["data", "isLoading", "isError", "isSuccess"],
    refetchInterval: (query) => {
      if (!caseId) return false;
      if (!query.state.data) return 1500; // Poll while loading to get initial data
      return query.state.data.some(
        (doc) => doc.status < DocumentStatus.Processed,
      )
        ? 1500
        : false; // Poll every 1.5 seconds if there are processing documents
    },
    select: (data) => {
      const docs = data
        .map((document) => addUploadDate(document as DocumentDetail))
        .filter(filterCompletedDocuments);
      return {
        documents: docs.filter(
          (doc) =>
            doc.parentRelationship !== ParentRelationship.SupplementalResponse,
        ),
        supplementalDocuments: docs.filter(
          (doc) =>
            doc.parentDocumentId &&
            doc.parentRelationship === ParentRelationship.SupplementalResponse,
        ),
      };
    },
  });

  const editDueDateMutation = useMutation({
    mutationFn: async (document: DocumentClient) => {
      if (document.info) {
        document.info.dueDateOverridden = dueDateOverride ?? undefined;
      }
      await documentApi.documentUpdateDocument({
        documentId: document.id ?? "",
        document: document ?? {},
      });
      return document;
    },
    onSuccess: (document) => {
      toast.success(`${document?.name ?? ""} was updated successfully.`);
      closeDueDateModal();
    },
    onError: (error: Error, document) => {
      toast.error(
        `Error updating ${document?.name ?? ""}. Please try again later.`,
      );
      rg4js("send", {
        error: normalizeError(error),
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        ...getUnarchivedDocumentListOptions(documentApi),
      });
      queryClient.invalidateQueries({
        ...getUnarchivedCaseDocumentListOptions(documentApi, caseId ?? ""),
      });
    },
  });

  const updateDocumentMutation = useMutation({
    mutationFn: async ({
      document,
      name,
    }: {
      document: DocumentClient;
      name: string;
    }) => {
      await documentApi.documentUpdateDocument({
        documentId: document.id,
        document: { ...document, name },
      });
      return { document, name };
    },
    onSuccess: ({ name }: { name: string }) => {
      toast.success(`${name} was updated successfully.`);
    },
    onError: ({ name }: { name: string }) => {
      toast.error(`Error updating ${name}. Please try again later.`);
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        ...getUnarchivedDocumentListOptions(documentApi),
      });
      queryClient.invalidateQueries({
        ...getUnarchivedCaseDocumentListOptions(documentApi, caseId ?? ""),
      });
    },
  });

  function getClientName(
    id: string | undefined | null,
    clients: Client[] | undefined,
  ) {
    if (!clients || !id) return "";
    return clients.find((c) => c.id === id)?.name ?? "";
  }

  const jurisdiction = useMemo(() => {
    if (!jurisdictions || !firm) {
      return undefined;
    }

    const firmJx =
      firm.offices?.find((x) => x.jurisdictions?.length)?.jurisdictions?.[0]
        ?.jurisdictionId ?? CaliforniaId;
    const jx = jurisdictions?.find((j) => j.id === firmJx);
    return jx;
  }, [jurisdictions, firm]);

  const statusesToFilter = [
    DocumentStatus.InfoConfirmed,
    DocumentStatus.Complete,
    caseId ? DocumentStatus.Processed : undefined,
    caseId ? DocumentStatus.Processing : undefined,
    caseId ? DocumentStatus.Uploaded : undefined,
    caseId ? DocumentStatus.ParsingComplete : undefined,
    caseId ? DocumentStatus.SplittingComplete : undefined,
    caseId ? DocumentStatus.Unknown : undefined,
  ];

  /* biome-ignore lint/correctness/useExhaustiveDependencies: we want to re-run the effect when the filters change */
  const filteredDocumentsResult = useMemo(() => {
    if (
      documentsQuery.isLoading ||
      clientsQuery.isLoading ||
      casesQuery.isLoading ||
      communicationsQuery.isLoading
    )
      return [];

    const filteredCompletedDocuments = documentsQuery.data?.documents?.filter(
      (document) => statusesToFilter.includes(document.status),
    );

    let filteredDocuments = filteredCompletedDocuments;
    if (nameFilter) {
      filteredDocuments = filteredDocuments?.filter(
        (document) =>
          document.name
            ?.toLocaleLowerCase()
            .includes(nameFilter.toLocaleLowerCase()) ||
          document.info?.propoundingPartyName?.includes(nameFilter) ||
          document.info?.respondingPartyName?.includes(nameFilter),
      );
    }

    if (partyFilter?.length) {
      filteredDocuments = filteredDocuments?.filter(
        (document) =>
          partyFilter.includes(document.info?.propoundingPartyName ?? "") ||
          partyFilter.includes(document.info?.respondingPartyName ?? ""),
      );
    }

    if (docFilter?.length) {
      filteredDocuments = filteredDocuments?.filter((document) =>
        docFilter.includes(
          getSubTypeText(
            document.info?.documentSubType ?? 0,
            getCaseJurisdiction(casesQuery.data, document.caseId ?? "") ?? "",
          ).short,
        ),
      );
    }

    if (clientFilter?.length) {
      filteredDocuments = filteredDocuments?.filter((document) =>
        clientFilter.includes(
          getClientName(document.clientId, clientsQuery.data ?? []),
        ),
      );
    }

    const recentCommunications = filteredDocuments?.filter((document) => {
      const comm = communicationsQuery.data?.find(
        (x) => x.objectId === document.id,
      );
      return (
        comm?.lastActivityDate &&
        comm.status === CommunicationStatus.Complete &&
        (!document.info?.lastViewDate ||
          comm.lastActivityDate < document.info.lastViewDate)
      );
    });

    if (recent && !caseId) {
      return filteredDocuments
        ?.filter((document) => recentCommunications?.includes(document))
        .slice(0, 10);
    }

    return filteredDocuments;
  }, [
    documentsQuery.isLoading,
    clientsQuery.isLoading,
    casesQuery.isLoading,
    communicationsQuery.isLoading,
    documentsQuery.data,
    clientsQuery.data,
    casesQuery.data,
    communicationsQuery.data,
    nameFilter,
    partyFilter,
    docFilter,
    clientFilter,
  ]);

  const shouldShowTable = useMemo(() => {
    const recentCommunications = documentsQuery.data?.documents?.filter((d) => {
      const comm = communicationsQuery.data?.find((x) => x.objectId === d.id);
      return (
        comm?.lastActivityDate &&
        comm.status === CommunicationStatus.Complete &&
        (!d.info?.lastViewDate || comm.lastActivityDate < d.info?.lastViewDate)
      );
    });
    return (recentCommunications && recentCommunications.length > 0) || !recent;
  }, [documentsQuery.data, communicationsQuery.data, recent]);

  const firmHasOnlyOneJx = useMemo(() => {
    // need to check for unique jurisdictions only
    const uniqueJurisdictionIds = (firm?.offices ?? []).reduce(
      (acc, office) => {
        for (const jurisdiction of office.jurisdictions ?? []) {
          if (jurisdiction.jurisdictionId) {
            acc.add(jurisdiction.jurisdictionId);
          }
        }
        return acc;
      },
      new Set<string>(),
    );
    return uniqueJurisdictionIds.size === 1;
  }, [firm?.offices]);

  /* table column definitions - need to be memoized to prevent re-rendering */
  /* biome-ignore lint/correctness/useExhaustiveDependencies: we want to re-run the effect when the columns change */
  const columns: ColumnDef<DocumentClient>[] = useMemo(
    () =>
      documentsQuery.isLoading || casesQuery.isLoading || clientsQuery.isLoading
        ? []
        : [
            {
              header: "",
              accessorKey: "source",
              meta: {
                width: "3%",
              },
              cell: ({ row }) => {
                return (
                  <div className={styles.sourceCell}>
                    {(row.original.interrogatoryNotificationCount || 0) > 0 && (
                      <div
                        data-testid="user-notification-indicator-container"
                        className={styles.flagContainer}
                      >
                        <UserNotificationIndicator
                          count={
                            row.original.interrogatoryNotificationCount ?? 0
                          }
                        />
                      </div>
                    )}
                    {row.original !== undefined &&
                    row.original.source === Source.InternalGenerated ? (
                      <WordIcon className="fs-2 text-primary" />
                    ) : (
                      <PdfIcon className="fs-2 text-danger" />
                    )}
                  </div>
                );
              },
            },
            {
              header: ({ column }) => {
                return (
                  <Button
                    className={styles.fieldHeaderButton}
                    onClick={() =>
                      column.toggleSorting(column.getIsSorted() === "asc")
                    }
                  >
                    Document Name
                    {column.getIsSorted() === "asc" ? (
                      <ChevronUp />
                    ) : (
                      <ChevronDown />
                    )}
                  </Button>
                );
              },
              meta: {
                width: showDueDates ? "15%" : "20%",
              },
              accessorKey: "name",
              accessorFn: (row) => row.name,
              cell: ({ row }) => {
                return (
                  <EditableText
                    value={row.original.name ?? ""}
                    onChange={(name) =>
                      updateDocumentMutation.mutate({
                        document: row.original,
                        name,
                      })
                    }
                  />
                );
              },
              sortingFn: "text",
            },
            {
              header: ({ column }) => {
                return (
                  <Button
                    className={styles.fieldHeaderButton}
                    onClick={() =>
                      column.toggleSorting(column.getIsSorted() === "asc")
                    }
                  >
                    Doc Type
                    {column.getIsSorted() === "asc" ? (
                      <ChevronUp />
                    ) : (
                      <ChevronDown />
                    )}
                  </Button>
                );
              },
              accessorKey: "type",
              accessorFn: (row) =>
                row.info?.documentSubType
                  ? getSubTypeText(
                      row.info.documentSubType,
                      getCaseJurisdiction(casesQuery.data, row.caseId ?? "") ??
                        "",
                    ).short
                  : "-",
              meta: {
                width: "10%",
              },
              cell: ({ row }) => {
                return (
                  <div className={styles.cellData}>
                    {!!row.original.info?.documentSubType &&
                      getSubTypeText(
                        row.original.info.documentSubType,
                        getCaseJurisdiction(
                          casesQuery.data,
                          row.original.caseId ?? "",
                        ) ?? "",
                      ).short}
                  </div>
                );
              },
              sortingFn: "text",
            },
            {
              header: ({ column }) => {
                if (caseId) return undefined; // only for dashboard
                return (
                  <Button
                    className={styles.fieldHeaderButton}
                    onClick={() =>
                      column.toggleSorting(column.getIsSorted() === "asc")
                    }
                  >
                    Case
                    {column.getIsSorted() === "asc" ? (
                      <ChevronUp />
                    ) : (
                      <ChevronDown />
                    )}
                  </Button>
                );
              },
              accessorKey: "case",
              accessorFn: (row) =>
                casesQuery.data?.find((c) => c.id === row.caseId)?.shortTitle ??
                "-",
              meta: {
                width: caseId ? "0%" : "13%",
              },
              cell: ({ row }) => {
                if (caseId) return undefined; // only for dashboard
                return (
                  <div className={clsx(styles.cellData, styles.truncateText)}>
                    {casesQuery.data?.find(
                      (c) => c.id === row.original.caseId,
                    ) ? (
                      <Link
                        title={
                          casesQuery.data?.find(
                            (c) => c.id === row.original.caseId,
                          )?.shortTitle ?? ""
                        }
                        to={`/cases/${casesQuery.data?.find((c) => c.id === row.original.caseId)?.id}`}
                      >
                        {
                          casesQuery.data?.find(
                            (c) => c.id === row.original.caseId,
                          )?.shortTitle
                        }
                      </Link>
                    ) : (
                      (row.original.info?.caseNumber ?? "-")
                    )}
                  </div>
                );
              },
              sortingFn: "text",
            },
            {
              header: ({ column }) => {
                if (!caseId) return undefined; // included only for case discovery
                return (
                  <Button
                    className={styles.fieldHeaderButton}
                    onClick={() =>
                      column.toggleSorting(column.getIsSorted() === "asc")
                    }
                  >
                    Propounding Party
                    {column.getIsSorted() === "asc" ? (
                      <ChevronUp />
                    ) : (
                      <ChevronDown />
                    )}
                  </Button>
                );
              },
              meta: {
                width: caseId ? "12%" : "0%",
              },
              accessorKey: "propoundingParty",
              accessorFn: (row) => row.info?.propoundingPartyName ?? "-",
              cell: ({ row }) => {
                if (!caseId) return undefined; // included only for case discovery
                return (
                  <div className={clsx(styles.cellData, styles.truncateText)}>
                    {row.original.info?.propoundingPartyName ?? "-"}
                  </div>
                );
              },
              sortingFn: "text",
            },
            {
              header: ({ column }) => {
                if (!caseId) return undefined; // included only for case discovery
                return (
                  <Button
                    className={styles.fieldHeaderButton}
                    onClick={() =>
                      column.toggleSorting(column.getIsSorted() === "asc")
                    }
                  >
                    Responding Party
                    {column.getIsSorted() === "asc" ? (
                      <ChevronUp />
                    ) : (
                      <ChevronDown />
                    )}
                  </Button>
                );
              },
              meta: {
                width: caseId ? "12%" : "0%",
              },
              accessorKey: "respondingParty",
              accessorFn: (row) => row.info?.respondingPartyName ?? "-",
              cell: ({ row }) => {
                if (!caseId) return undefined; // included only for case discovery
                return (
                  <div className={clsx(styles.cellData, styles.truncateText)}>
                    {row.original.info?.respondingPartyName ?? "-"}
                  </div>
                );
              },
              sortingFn: "text",
            },
            {
              header: ({ column }) => {
                if (firmHasOnlyOneJx) return undefined;
                return (
                  <Button
                    className={styles.fieldHeaderButton}
                    onClick={() =>
                      column.toggleSorting(column.getIsSorted() === "asc")
                    }
                  >
                    Jx
                    {column.getIsSorted() === "asc" ? (
                      <ChevronUp />
                    ) : (
                      <ChevronDown />
                    )}
                  </Button>
                );
              },
              accessorKey: "jx",
              accessorFn: (row) =>
                jurisdictions?.find(
                  (j) =>
                    j.id ===
                    casesQuery.data?.find((c) => c.id === row.caseId)
                      ?.jurisdiction,
                )?.abbreviation ?? "-",
              meta: {
                width: firmHasOnlyOneJx ? "0%" : "5%",
              },
              cell: ({ row }) => {
                if (firmHasOnlyOneJx) return undefined;
                return (
                  <div className={styles.cellData}>
                    {jurisdictions?.find(
                      (j) =>
                        j.id ===
                        casesQuery.data?.find(
                          (c) => c.id === row.original.caseId,
                        )?.jurisdiction,
                    )?.abbreviation ?? "-"}
                  </div>
                );
              },
              sortingFn: "text",
            },
            {
              header: ({ column }) => {
                return (
                  <Button
                    className={styles.fieldHeaderButton}
                    onClick={() =>
                      column.toggleSorting(column.getIsSorted() === "asc")
                    }
                  >
                    Upload Date
                    {column.getIsSorted() === "asc" ? (
                      <ChevronUp />
                    ) : (
                      <ChevronDown />
                    )}
                  </Button>
                );
              },
              accessorKey: "uploadDate",
              accessorFn: (row) =>
                row.uploadDate ? toLocaleUSDateString(row.uploadDate) : "-",
              meta: {
                width: firmHasOnlyOneJx ? "10%" : "5%",
              },
              cell: ({ row }) => {
                return (
                  <div className={styles.cellData}>
                    {row.original.uploadDate
                      ? toLocaleUSDateString(row.original.uploadDate)
                      : "-"}
                  </div>
                );
              },
              sortingFn: (rowA, rowB) => {
                return (
                  rowA.original.uploadDate.getTime() -
                  rowB.original.uploadDate.getTime()
                );
              },
            },
            /* due date column, only shown if jurisdiction supports due dates */
            {
              header: ({ column }) => {
                if (!showDueDates) return undefined;
                return (
                  <Button
                    className={styles.fieldHeaderButton}
                    onClick={() =>
                      column.toggleSorting(column.getIsSorted() === "asc")
                    }
                  >
                    Due Date
                    {column.getIsSorted() === "asc" ? (
                      <ChevronUp />
                    ) : (
                      <ChevronDown />
                    )}
                  </Button>
                );
              },
              accessorKey: "realDueDate",
              accessorFn: (row) =>
                row.info?.dueDateOverridden || row.info?.dueDate || "-",
              meta: {
                width: showDueDates ? "10%" : "0%",
              },
              cell: ({ row }) => {
                if (
                  !showDueDates ||
                  (!caseId &&
                    !casesSupportingDueDates?.some(
                      (c) => c.id === row.original.caseId,
                    ))
                )
                  return undefined;
                return (
                  <div className={styles.cellData}>
                    {toLocaleUSDateString(
                      row.original.info?.dueDateOverridden ??
                        row.original.info?.dueDate ??
                        "",
                    ) || (
                      <Button
                        variant="link"
                        size="sm"
                        className={styles.setDueDateButton}
                        onClick={() => editDueDate(row.original)}
                        style={{ padding: 0 }}
                      >
                        Set Due Date
                      </Button>
                    )}
                  </div>
                );
              },
              sortingFn: (rowA, rowB) => {
                return (
                  new Date(
                    rowA.original.info?.dueDateOverridden ??
                      rowA.original.info?.dueDate ??
                      "",
                  ).getTime() -
                  new Date(
                    rowB.original.info?.dueDateOverridden ??
                      rowB.original.info?.dueDate ??
                      "",
                  ).getTime()
                );
              },
            },
            {
              header: ({ column }) => {
                return (
                  <Button
                    className={styles.fieldHeaderButton}
                    onClick={() =>
                      column.toggleSorting(column.getIsSorted() === "asc")
                    }
                  >
                    Client
                    {column.getIsSorted() === "asc" ? (
                      <ChevronUp />
                    ) : (
                      <ChevronDown />
                    )}
                  </Button>
                );
              },
              accessorKey: "client",
              accessorFn: (row) =>
                clientsQuery.data?.find((c) => c.id === row.clientId)?.name ??
                "-",
              meta: {
                width: "12%",
              },
              cell: ({ row }) => {
                return (
                  <div className={clsx(styles.cellData, styles.truncateText)}>
                    {clientsQuery.data?.find(
                      (c) => c.id === row.original.clientId,
                    ) ? (
                      <Button
                        variant="link"
                        size="sm"
                        to={`/clients/${clientsQuery.data?.find((c) => c.id === row.original.clientId)?.id}`}
                        style={{ padding: 0 }}
                      >
                        {clientsQuery.data?.find(
                          (c) => c.id === row.original.clientId,
                        )?.name ?? ""}
                      </Button>
                    ) : (
                      "-"
                    )}
                  </div>
                );
              },
              sortingFn: "text",
            },
            {
              header: ({ column }) => (
                <Button
                  className={styles.fieldHeaderButton}
                  onClick={() =>
                    column.toggleSorting(
                      !column.getIsSorted() || column.getIsSorted() === "asc",
                    )
                  }
                >
                  Client Activity
                  {column.getIsSorted() !== "desc" ? (
                    <ChevronUp />
                  ) : (
                    <ChevronDown />
                  )}
                </Button>
              ),
              accessorKey: "clientActivity",
              accessorFn: (row) =>
                communicationsQuery.data?.find((x) => x.objectId === row.id)
                  ?.status ?? "-",
              meta: {
                // make room for due date column is shown
                // otherwise use 27% for dashboard and 16% for case discovery to make room for propounding and responding parties
                width: showDueDates ? "17%" : caseId ? "16%" : "27%",
                reviewExpanded: false,
              },
              cell: ({ row }) => (
                <div className={styles.cellData}>
                  {communicationsQuery.data?.find(
                    (x) => x.objectId === row.original.id,
                  ) && (
                    <DocumentListTableRowCommunication
                      setSelectedClient={(client) => setSelectedClient(client)}
                      selectedClient={selectedClient}
                      setShowEmailModal={(show) => setShowEmailModal(show)}
                      showEmailModal={showEmailModal}
                      communication={
                        communicationsQuery.data?.find(
                          (x) => x.objectId === row.original.id,
                        ) as ViewCommunication
                      }
                      document={row.original}
                      client={clientsQuery.data?.find(
                        (c) => c.id === row.original.clientId,
                      )}
                      reviewExpanded={expandedRows.includes(row.original.id)}
                      setReviewExpanded={() => {
                        setExpandedRows((prev) =>
                          prev.includes(row.original.id)
                            ? prev.filter((id) => id !== row.original.id)
                            : [...prev, row.original.id],
                        );
                      }}
                    />
                  )}
                </div>
              ),
              sortingFn: (rowA, rowB) => {
                const lastActivityDateA =
                  communicationsQuery.data
                    ?.find((x) => x.objectId === rowA.original.id)
                    ?.lastActivityDate?.getTime() ?? 0;
                const lastActivityDateB =
                  communicationsQuery.data
                    ?.find((x) => x.objectId === rowB.original.id)
                    ?.lastActivityDate?.getTime() ?? 0;
                return lastActivityDateA - lastActivityDateB;
              },
            },
            {
              header: "",
              accessorKey: "actions",
              meta: {
                width: "5%",
              },
              cell: ({ row }) => {
                return (
                  <div className="d-flex justify-content-end gap-2 align-items-center">
                    <DocumentAction document={row.original} />
                    {(row.original.status ?? 0) >= DocumentStatus.Processed && (
                      <DocumentActionsMenu
                        editDueDate={editDueDate}
                        setAddToCalendarDocument={setAddToCalendarDocument}
                        client={clientsQuery.data?.find(
                          (c) => c.id === row.original.clientId,
                        )}
                        document={row.original}
                        _case={casesQuery.data?.find(
                          (c) => c.id === row.original.caseId,
                        )}
                        sendReminder={
                          canSendReminderEmail(
                            communicationsQuery.data?.find(
                              (x) => x.objectId === row.original.id,
                            ) as ViewCommunicationResponse,
                          )
                            ? () => setShowEmailModal(true)
                            : undefined
                        }
                        setSelectedClient={(client) =>
                          setSelectedClient(client)
                        }
                        communication={
                          communicationsQuery.data?.find(
                            (x) => x.objectId === row.original.id,
                          ) as ViewCommunicationResponse
                        }
                      />
                    )}
                  </div>
                );
              },
            },
          ],
    [
      documentsQuery.isLoading,
      communicationsQuery.isLoading,
      communicationsQuery.data,
      casesQuery.isLoading,
      clientsQuery.isLoading,
      clientsQuery.data,
      casesQuery.data,
      selectedClient,
      showEmailModal,
      expandedRows,
      firmHasOnlyOneJx,
      caseId,
      caseJurisdiction,
    ],
  );

  const table = useReactTable({
    data: filteredDocumentsResult ?? [],
    getSortedRowModel: getSortedRowModel(),
    columns: columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    autoResetPageIndex: false,
    autoResetAll: false,
    initialState: {
      sorting: [{ id: "uploadDate", desc: true }],
      pagination: {
        pageIndex: 0,
        pageSize: 20,
      },
    },
  });
  return (
    <Loading
      isLoading={
        !recent &&
        (documentsQuery.isLoading ||
          communicationsQuery.isLoading ||
          casesQuery.isLoading ||
          clientsQuery.isLoading)
      }
    >
      <ReminderEmailModal
        isShow={showEmailModal}
        onClose={() => setShowEmailModal(false)}
        clientId={selectedClient?.id}
        selectedClient={selectedClient}
      />

      {addToCalendarDocument && (
        <AddToCalendarModal
          isShow={true}
          doc={addToCalendarDocument ?? ({} as DocumentClient)}
          client={clientsQuery.data?.find(
            (c) => c.id === addToCalendarDocument?.clientId,
          )}
          _case={casesQuery.data?.find(
            (c) => c.id === addToCalendarDocument?.caseId,
          )}
          onClose={() => setAddToCalendarDocument(undefined)}
        />
      )}
      <FormModal
        show={!!editDueDateDocument}
        title={`${editDueDateDocument?.info?.dueDateOverridden || editDueDateDocument?.info?.dueDate ? "Edit" : "Set"} Due Date`}
        size="sm"
        confirmText="Confirm"
        cancelText="Cancel"
        onClose={closeDueDateModal}
        onConfirm={() =>
          editDueDateDocument && editDueDateMutation.mutate(editDueDateDocument)
        }
      >
        {(editDueDateDocument?.info?.dueDateOverridden ||
          editDueDateDocument?.info?.dueDate) && (
          <div className="d-flex justify-content-center mb-3 gap-2 align-items-center">
            Add{" "}
            <Button
              variant="link"
              className={styles.addDatesButton}
              onClick={() => addDueDateDays(editDueDateDocument, 7)}
            >
              7 days
            </Button>
            <Button
              variant="link"
              className={styles.addDatesButton}
              onClick={() => addDueDateDays(editDueDateDocument, 14)}
            >
              14 days
            </Button>
            <Button
              onClick={() => addDueDateDays(editDueDateDocument)}
              className={styles.addDatesButton}
              variant="link"
            >
              1 month
            </Button>
          </div>
        )}
        <Form.Control
          type="date"
          className="date-picker"
          value={
            dueDateOverride ||
            editDueDateDocument?.info?.dueDateOverridden ||
            editDueDateDocument?.info?.dueDate ||
            undefined
          }
          onChange={(e) => setDueDateOverride(e.target.value)}
        />
      </FormModal>

      {shouldShowTable && (
        <>
          {!caseId && table.getRowModel().rows?.length > 0 && (
            <h4
              className="table-headers"
              data-testid={
                recent
                  ? "recent-client-activity-header"
                  : "document-list-header"
              }
            >
              {recent ? "Recent Client Activity" : "Document List"}
            </h4>
          )}
          {table.getRowModel().rows?.length === 0 ? (
            !caseId ? (
              <EmptyList
                jurisdictionId={jurisdiction?.id ?? ""}
                chooseFile={chooseFile ? (e) => chooseFile(e) : () => {}}
              />
            ) : (
              <div
                data-testid="no-documents-found"
                className={styles.noDocumentsFound}
              >
                No documents {nameFilter ? `named "${nameFilter}"` : ""} found
                for this case.
                <Button
                  variant="link"
                  size="default"
                  className="ms-1 p-0"
                  onClick={chooseFile ?? (() => {})}
                >
                  Upload one here
                </Button>
              </div>
            )
          ) : (
            <>
              <Table
                className={clsx(
                  styles.documentListTable,
                  recent && shouldShowTable && "mb-4",
                )}
              >
                <TableHeader>
                  {table.getHeaderGroups().map((headerGroup) => (
                    <TableRow
                      className={styles.tableHeaderRow}
                      key={headerGroup.id}
                    >
                      {headerGroup.headers.map((header) => {
                        return (
                          <TableHead key={header.id}>
                            {header.isPlaceholder
                              ? null
                              : flexRender(
                                  header.column.columnDef.header,
                                  header.getContext(),
                                )}
                          </TableHead>
                        );
                      })}
                    </TableRow>
                  ))}
                </TableHeader>
                <TableBody>
                  {table.getRowModel().rows.map((row) => (
                    <React.Fragment key={row.original.id}>
                      <DocumentListTableRow
                        row={row}
                        communications={communicationsQuery.data ?? []}
                        client={clientsQuery.data?.find(
                          (c) => c.id === row.original.clientId,
                        )}
                        reviewExpanded={expandedRows.includes(row.original.id)}
                        setReviewExpanded={() => {
                          setExpandedRows((prev) =>
                            prev.includes(row.original.id)
                              ? prev.filter((id) => id !== row.original.id)
                              : [...prev, row.original.id],
                          );
                        }}
                      />
                      {/* find supplemental documents for the current document */}
                      {documentsQuery.data?.supplementalDocuments
                        ?.filter(
                          (doc) => doc.parentDocumentId === row.original.id,
                        )
                        .sort(
                          (a, b) =>
                            a.uploadDate.getTime() - b.uploadDate.getTime(),
                        )
                        .map((doc) => (
                          <DocumentListTableSupplementalRow
                            key={doc.id}
                            row={row}
                            document={doc}
                            client={clientsQuery.data?.find(
                              (c) => c.id === doc.clientId,
                            )}
                            case={casesQuery.data?.find(
                              (c) => c.id === doc.caseId,
                            )}
                            sendReminder={
                              canSendReminderEmail(
                                communicationsQuery.data?.find(
                                  (x) => x.objectId === doc.id,
                                ) as ViewCommunicationResponse,
                              )
                                ? () => setShowEmailModal(true)
                                : undefined
                            }
                            setSelectedClient={(client) =>
                              setSelectedClient(client)
                            }
                            communication={
                              communicationsQuery.data?.find(
                                (x) => x.objectId === doc.id,
                              ) as ViewCommunicationResponse
                            }
                            reviewExpanded={expandedRows.includes(doc.id)}
                            setReviewExpanded={() => {
                              setExpandedRows((prev) =>
                                prev.includes(doc.id)
                                  ? prev.filter((id) => id !== doc.id)
                                  : [...prev, doc.id],
                              );
                            }}
                          />
                        ))}
                    </React.Fragment>
                  ))}
                </TableBody>
              </Table>
              <div className="d-flex justify-content-end pt-2">
                {!recent && table.getPageCount() > 1 && (
                  <Pagination
                    navigate={(page) => table.setPageIndex(page - 1)}
                    currentPage={table.getState().pagination.pageIndex + 1}
                    totalPages={table.getPageCount()}
                    totalItemCount={filteredDocumentsResult?.length ?? 0}
                    itemsPerPage={table.getState().pagination.pageSize}
                    showPageCount
                    showInput
                  />
                )}
              </div>
            </>
          )}
        </>
      )}
    </Loading>
  );
}
